import { useDrag } from '@use-gesture/react';
import clsx from 'clsx';
import { type KeyboardEventHandler, useImperativeHandle, useRef } from 'react';

import { selectionArea } from './SelectionArea.sty.js';
import { SELECTED_AREA_RADIUS } from '../overlays/constants.js';
import { useSelectionAreaPathRef } from './hooks/use-selection-area-path-ref.js';

interface SelectionBackdropProps {
  focusable: boolean;
  startX: number;
  endX: number;
  height: number;
  isSynchronizedSelection: boolean;
  interactable: boolean;
  handleSelectionAreaKeyDown?: KeyboardEventHandler;
  onDragMove?: (x: number) => void;
}

const createSelectionAreaOutline = (
  startX: number,
  endX: number,
  height: number,
  radius: number,
): string => {
  const selectionWidth = Math.abs(startX - endX);
  const minimumSelectionSize = 2 * radius * 0.75;
  const calculatedRadius =
    selectionWidth > minimumSelectionSize
      ? radius
      : Math.floor(selectionWidth * 0.75);

  return `
    M${startX + calculatedRadius},1
    H${endX - calculatedRadius}
    A${calculatedRadius},${calculatedRadius} 0 0 1 ${endX},${calculatedRadius}
    V${height}
    H${startX}
    V${calculatedRadius}
    A${calculatedRadius},${calculatedRadius} 0 0 1 ${
    startX + calculatedRadius
  },1
    Z
  `;
};

export const SelectionBackdrop = ({
  startX,
  endX,
  height,
  focusable,
  interactable,
  handleSelectionAreaKeyDown,
  onDragMove,
  isSynchronizedSelection,
}: SelectionBackdropProps) => {
  const selectionAreaRef = useRef<SVGPathElement>(null);
  const forwardedRef = useSelectionAreaPathRef();
  useImperativeHandle(
    forwardedRef,
    () => ({
      element: selectionAreaRef.current,
      focus() {
        selectionAreaRef.current?.focus();
      },
    }),
    [],
  );

  const dragHandlers = useDrag(
    ({ type, delta }: { type: string; delta: number[] }) => {
      if (!(type === 'pointerdown' || type === 'pointerup')) {
        onDragMove?.(delta[0]);
      }
    },
    {},
  );

  return (
    <path
      ref={selectionAreaRef}
      tabIndex={focusable ? 0 : -1}
      onKeyDown={handleSelectionAreaKeyDown}
      strokeDasharray={isSynchronizedSelection ? '3,2' : '0,0'}
      className={clsx(selectionArea)}
      pointerEvents={interactable ? 'all' : 'none'}
      d={createSelectionAreaOutline(startX, endX, height, SELECTED_AREA_RADIUS)}
      {...dragHandlers()}
    />
  );
};
